import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { getPhonemeMap } from "./phoneme-mapping";
import { MorphScheme } from "../helpers/detect-morph-scheme";
import { logger } from "../../common/logger";

/**
 * DEV-ONLY viseme tuning overlay. Rendered only when the URL contains
 * `?visemeDebug=1`, so it has zero effect on normal usage.
 *
 * Purpose: tune lip-sync shapes by SEEING them instead of guessing.
 *  - Slide any morph 0..1 to learn what it does on the loaded model.
 *  - "Apply phoneme" composes a phoneme from the current map and freezes it.
 *  - "Copy active JSON" copies the non-zero morphs so tuned values can be
 *    pasted straight into phoneme-mapping.tsx.
 *
 * It writes directly to each mesh's morphTargetInfluences every frame, so the
 * pose holds while the avatar is idle (mouth morphs aren't driven by the idle
 * body animation). Use it while the avatar is NOT speaking.
 */
interface VisemeDebugPanelProps {
  morphTarget: any;
  morphScheme: MorphScheme;
}

interface MorphRef {
  influences: number[];
  index: number;
}

export function VisemeDebugPanel({
  morphTarget,
  morphScheme,
}: VisemeDebugPanelProps) {
  // name -> every (influences array, index) that morph appears at, across meshes
  const targets = useMemo(() => {
    const map: Record<string, MorphRef[]> = {};
    if (!morphTarget?.traverse) return map;
    morphTarget.traverse((obj: any) => {
      if (obj.isMesh && obj.morphTargetDictionary && obj.morphTargetInfluences) {
        for (const [name, index] of Object.entries(obj.morphTargetDictionary)) {
          (map[name] ||= []).push({
            influences: obj.morphTargetInfluences,
            index: index as number,
          });
        }
      }
    });
    return map;
  }, [morphTarget]);

  const morphNames = useMemo(() => Object.keys(targets).sort(), [targets]);

  const [overrides, setOverrides] = useState<Record<string, number>>({});
  const [filter, setFilter] = useState("");
  const [collapsed, setCollapsed] = useState(false);
  const overridesRef = useRef(overrides);
  overridesRef.current = overrides;

  // Hold the pose: apply overrides to the live influence arrays every frame.
  useEffect(() => {
    if (!morphNames.length) return;
    let raf = 0;
    const apply = () => {
      const ov = overridesRef.current;
      for (const name in ov) {
        const entries = targets[name];
        if (!entries) continue;
        const value = ov[name];
        for (const e of entries) e.influences[e.index] = value;
      }
      raf = requestAnimationFrame(apply);
    };
    raf = requestAnimationFrame(apply);
    return () => {
      cancelAnimationFrame(raf);
      // release the pose back to neutral on unmount
      for (const name in targets) {
        for (const e of targets[name]) e.influences[e.index] = 0;
      }
    };
  }, [targets, morphNames.length]);

  const phonemeMap = getPhonemeMap(morphScheme);
  const phonemes = useMemo(() => Object.keys(phonemeMap), [phonemeMap]);
  const [selectedPhoneme, setSelectedPhoneme] = useState(phonemes[0] || "aa");

  const setMorph = (name: string, value: number) =>
    setOverrides((prev) => ({ ...prev, [name]: value }));

  const clearAll = () => {
    const next: Record<string, number> = {};
    morphNames.forEach((n) => (next[n] = 0));
    setOverrides(next);
  };

  const applyPhoneme = (ph: string) => {
    const shape = phonemeMap[ph] || {};
    const next: Record<string, number> = {};
    morphNames.forEach((n) => (next[n] = 0));
    Object.entries(shape).forEach(([k, v]) => {
      if (k in targets) next[k] = v as number;
    });
    setOverrides(next);
  };

  const [pasteText, setPasteText] = useState("");
  const applyPastedJson = () => {
    try {
      const parsed = JSON.parse(pasteText);
      const next: Record<string, number> = {};
      morphNames.forEach((n) => (next[n] = 0));
      Object.entries(parsed).forEach(([k, v]) => {
        if (k in targets && typeof v === "number") next[k] = v;
      });
      setOverrides(next);
    } catch {
      // eslint-disable-next-line no-console
      console.warn("[visemeDebug] invalid JSON pasted");
    }
  };

  const copyActive = () => {
    const active: Record<string, number> = {};
    Object.entries(overridesRef.current).forEach(([k, v]) => {
      if (v > 0.001) active[k] = Math.round(v * 100) / 100;
    });
    const json = JSON.stringify(active);
    navigator.clipboard?.writeText(json).catch(() => {});
    logger.log("[visemeDebug] active morphs:", json);
  };

  if (!morphTarget) {
    return (
      <div style={panelStyle}>
        <div style={{ fontWeight: 700 }}>Viseme Debug</div>
        <div style={{ opacity: 0.7, marginTop: 8 }}>Waiting for model…</div>
      </div>
    );
  }

  const shown = morphNames.filter((n) =>
    n.toLowerCase().includes(filter.toLowerCase())
  );
  const activeCount = Object.values(overrides).filter((v) => v > 0.001).length;

  return (
    <div style={panelStyle}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <div style={{ fontWeight: 700 }}>
          Viseme Debug · {morphScheme} · {morphNames.length} morphs
        </div>
        <button style={btn} onClick={() => setCollapsed((c) => !c)}>
          {collapsed ? "▼" : "▲"}
        </button>
      </div>

      {!collapsed && (
        <>
          <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
            <select
              style={{ ...input, flex: 1 }}
              value={selectedPhoneme}
              onChange={(e) => setSelectedPhoneme(e.target.value)}
            >
              {phonemes.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <button style={btn} onClick={() => applyPhoneme(selectedPhoneme)}>
              Apply
            </button>
          </div>

          <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
            <button style={{ ...btn, flex: 1 }} onClick={clearAll}>
              Clear all
            </button>
            <button style={{ ...btn, flex: 1 }} onClick={copyActive}>
              Copy JSON ({activeCount})
            </button>
          </div>

          <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
            <textarea
              style={{ ...input, flex: 1, height: 34, resize: "vertical" }}
              placeholder='paste shape JSON e.g. {"AI":0.6,"LowerTeeth_Down":0.5}'
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
            />
            <button style={btn} onClick={applyPastedJson}>
              Apply JSON
            </button>
          </div>

          <input
            style={{ ...input, width: "100%", marginBottom: 8 }}
            placeholder="filter morphs…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />

          <div>
            {shown.map((name) => {
              const value = overrides[name] ?? 0;
              const active = value > 0.001;
              return (
                <div
                  key={name}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 3,
                    opacity: active ? 1 : 0.72,
                  }}
                >
                  <span
                    style={{
                      width: 150,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: active ? "#8fd0ff" : "#e6e8ef",
                    }}
                    title={name}
                  >
                    {name}
                  </span>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={value}
                    onChange={(e) => setMorph(name, parseFloat(e.target.value))}
                    style={{ flex: 1 }}
                  />
                  <span style={{ width: 30, textAlign: "right" }}>
                    {value.toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

const panelStyle: CSSProperties = {
  position: "fixed",
  top: 0,
  right: 0,
  width: 360,
  maxHeight: "100vh",
  overflowY: "auto",
  background: "rgba(10,12,20,0.93)",
  color: "#e6e8ef",
  font: "12px ui-monospace, Menlo, Consolas, monospace",
  padding: 12,
  zIndex: 99999,
  boxShadow: "-4px 0 24px rgba(0,0,0,0.5)",
};

const btn: CSSProperties = {
  background: "#2a3350",
  color: "#e6e8ef",
  border: "1px solid #3a4666",
  borderRadius: 4,
  padding: "4px 8px",
  cursor: "pointer",
  fontFamily: "inherit",
  fontSize: 12,
};

const input: CSSProperties = {
  background: "#161a26",
  color: "#e6e8ef",
  border: "1px solid #333a52",
  borderRadius: 4,
  padding: "3px 6px",
  fontFamily: "inherit",
  fontSize: 12,
};
